import React, { useState, useRef } from "react";
import {
  Dialog,
  TextField,
  Box,
  IconButton,
  FormControlLabel,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import axiosInstance from "../../../utils/config/config";
import {
  AGREEMENT_MODAL_TITLE,
  AGREEMENT_VERSION,
  SIGN_BUTTON_TEXT,
  CANCEL_BUTTON_TEXT,
  SIGNING_TEXT,
  SIGNATURE_LABEL,
  SIGNATURE_PLACEHOLDER,
  CHECKBOX_LABEL,
  SUCCESS_MESSAGE,
  ERROR_MESSAGE,
  AGREEMENT_CONTENT,
  GUARDIAN_SECTION_TITLE,
  GUARDIAN_SECTION_NOTICE,
  GUARDIAN_NAME_LABEL,
  GUARDIAN_NAME_PLACEHOLDER,
  GUARDIAN_SIGNATURE_LABEL,
  GUARDIAN_SIGNATURE_PLACEHOLDER,
  GUARDIAN_RELATIONSHIP_LABEL,
  GUARDIAN_EMAIL_LABEL,
  GUARDIAN_EMAIL_PLACEHOLDER,
  GUARDIAN_RELATIONSHIP_OPTIONS,
} from "./AgreementSigningModal.constants";
import {
  StyledDialogTitle,
  StyledTitleText,
  StyledDialogContent,
  AgreementScrollBox,
  AgreementSectionHeading,
  AgreementSectionSubtitle,
  AgreementText,
  AgreementListItem,
  SignatureSection,
  SignatureSectionTitle,
  StyledCheckbox,
  CheckboxLabel,
  DateDisplay,
  ActionBox,
  SignButton,
  CancelButton,
  SuccessBanner,
  SuccessText,
  GuardianSection,
  GuardianSectionTitle,
  GuardianNotice,
  GuardianNoticeText,
} from "./AgreementSigningModal.styles";

/** Simple email format check (mirrors backend validation). */
const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

/**
 * Signature / name field validation rules.
 * - Max length 255 matches backend DB cap (Sequelize STRING default = VARCHAR(255)).
 * - Allowed: letters (including accented À-ÿ), spaces, hyphens, apostrophes, periods.
 */
const SIGNATURE_MAX_LENGTH = 255;
const SIGNATURE_ALLOWED_REGEX = /^[A-Za-zÀ-ÿ\s'.-]*$/;
const SIGNATURE_INVALID_CHARS = /[^A-Za-zÀ-ÿ\s'.-]/g;
const SIGNATURE_INVALID_CHARS_MSG =
  "Only letters, spaces, hyphens, apostrophes, and periods are allowed";
const SIGNATURE_MAX_LENGTH_MSG = `Maximum ${SIGNATURE_MAX_LENGTH} characters allowed`;
const SIGNATURE_MIN_LENGTH_MSG = "Please enter your full legal name";

/** Strips disallowed characters and caps length. */
const sanitizeSignature = (value) =>
  value.replace(SIGNATURE_INVALID_CHARS, "").slice(0, SIGNATURE_MAX_LENGTH);

/** Returns an error message for the given (already-sanitized) value, or "" if valid. */
const getSignatureError = (value) => {
  if (!value) return "";
  if (!SIGNATURE_ALLOWED_REGEX.test(value)) return SIGNATURE_INVALID_CHARS_MSG;
  if (value.length > SIGNATURE_MAX_LENGTH) return SIGNATURE_MAX_LENGTH_MSG;
  const trimmed = value.trim();
  if (trimmed.length > 0 && trimmed.length < 2) return SIGNATURE_MIN_LENGTH_MSG;
  return "";
};

const INITIAL_GUARDIAN_STATE = {
  name: "",
  signature: "",
  relationship: "",
  email: "",
};

const AgreementSigningModal = ({ open, onClose, onSigned, isMinor = false }) => {
  const [signedName, setSignedName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const successTimerRef = useRef(null);

  // Per-field validation errors for the signature / name fields
  const [signedNameError, setSignedNameError] = useState("");
  const [guardianNameError, setGuardianNameError] = useState("");
  const [guardianSignatureError, setGuardianSignatureError] = useState("");

  // Parent / Legal Guardian fields (only used when isMinor is true)
  const [guardian, setGuardian] = useState(INITIAL_GUARDIAN_STATE);

  const updateGuardian = (field, value) =>
    setGuardian((prev) => ({ ...prev, [field]: value }));

  /**
   * Sanitize a raw input value and compute the appropriate user-facing error.
   * If the raw value contained disallowed characters or exceeded the max length,
   * we flash a specific error message so the user understands why the field
   * content changed.
   */
  const processSignatureInput = (raw) => {
    const clean = sanitizeSignature(raw);
    let errMsg = "";
    if (raw.length > SIGNATURE_MAX_LENGTH) {
      errMsg = SIGNATURE_MAX_LENGTH_MSG;
    } else if (raw !== clean) {
      errMsg = SIGNATURE_INVALID_CHARS_MSG;
    } else {
      errMsg = getSignatureError(clean);
    }
    return { clean, errMsg };
  };

  const handleSignedNameChange = (e) => {
    const { clean, errMsg } = processSignatureInput(e.target.value);
    setSignedName(clean);
    setSignedNameError(errMsg);
  };

  const handleGuardianNameChange = (e) => {
    const { clean, errMsg } = processSignatureInput(e.target.value);
    updateGuardian("name", clean);
    setGuardianNameError(errMsg);
  };

  const handleGuardianSignatureChange = (e) => {
    const { clean, errMsg } = processSignatureInput(e.target.value);
    updateGuardian("signature", clean);
    setGuardianSignatureError(errMsg);
  };

  // --- Validation ---
  const clientFieldsValid =
    signedName.trim().length >= 2 &&
    !getSignatureError(signedName) &&
    agreed;

  const guardianFieldsValid = isMinor
    ? guardian.name.trim().length >= 2 &&
      guardian.signature.trim().length >= 2 &&
      !getSignatureError(guardian.name) &&
      !getSignatureError(guardian.signature) &&
      guardian.relationship.trim().length > 0 &&
      guardian.email.trim().length > 0 &&
      isValidEmail(guardian.email.trim())
    : true;

  const canSign = clientFieldsValid && guardianFieldsValid && !signing;

  // --- Handlers ---
  const handleSign = async () => {
    try {
      setSigning(true);
      setError(null);

      const payload = {
        signedName: signedName.trim(),
        agreementVersion: AGREEMENT_VERSION,
      };

      // Include guardian fields for minor clients
      if (isMinor) {
        payload.guardianName = guardian.name.trim();
        payload.guardianSignature = guardian.signature.trim();
        payload.guardianRelationship = guardian.relationship.trim();
        payload.guardianEmail = guardian.email.trim();
      }

      const response = await axiosInstance.post("agreements/sign", payload);

      if (response.data.success) {
        setSigning(false);
        setSuccess(true);
        successTimerRef.current = setTimeout(() => {
          if (onSigned) onSigned(response.data.agreement);
          // Reset state after callback
          setSignedName("");
          setAgreed(false);
          setGuardian(INITIAL_GUARDIAN_STATE);
          setSignedNameError("");
          setGuardianNameError("");
          setGuardianSignatureError("");
          setError(null);
          setSuccess(false);
          onClose();
        }, 1500);
      } else {
        setSigning(false);
        setError(response.data.message || ERROR_MESSAGE);
      }
    } catch (err) {
      setSigning(false);
      setError(err.response?.data?.message || err.message || ERROR_MESSAGE);
    }
  };

  const handleClose = () => {
    if (signing) return;
    if (successTimerRef.current) clearTimeout(successTimerRef.current);
    setSignedName("");
    setAgreed(false);
    setGuardian(INITIAL_GUARDIAN_STATE);
    setSignedNameError("");
    setGuardianNameError("");
    setGuardianSignatureError("");
    setError(null);
    setSuccess(false);
    onClose();
  };

  const todayFormatted = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  /** Shared sx for all text fields in the modal. */
  const textFieldSx = {
    mb: 2,
    "& .MuiOutlinedInput-root": {
      fontFamily: "'Gotham', sans-serif",
      borderRadius: "10px",
    },
    "& .MuiInputLabel-root": {
      fontFamily: "'Gotham', sans-serif",
    },
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <StyledDialogTitle>
        <StyledTitleText>{AGREEMENT_MODAL_TITLE}</StyledTitleText>
        <IconButton
          onClick={handleClose}
          disabled={signing}
          sx={{ color: "inherit" }}
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <StyledDialogContent>
        {success && (
          <SuccessBanner>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
              <CheckCircleIcon sx={{ color: "#2E7D32" }} />
              <SuccessText>{SUCCESS_MESSAGE}</SuccessText>
            </Box>
          </SuccessBanner>
        )}

        {/* Agreement Content */}
        <AgreementScrollBox>
          <AgreementSectionHeading sx={{ fontSize: "1.15rem", marginBottom: 2 }}>
            {AGREEMENT_CONTENT.title}
          </AgreementSectionHeading>
          <AgreementText sx={{ fontStyle: "italic", marginBottom: 2 }}>
            Effective: {AGREEMENT_CONTENT.effectiveDate}
          </AgreementText>

          {AGREEMENT_CONTENT.sections.map((section, index) => (
            <Box key={index}>
              <AgreementSectionHeading>{section.heading}</AgreementSectionHeading>
              {section.subtitle && (
                <AgreementSectionSubtitle>
                  {section.subtitle}
                </AgreementSectionSubtitle>
              )}
              {section.content && (
                <AgreementText>{section.content}</AgreementText>
              )}
              {section.items && (
                <ul style={{ paddingLeft: 24, margin: "8px 0" }}>
                  {section.items.map((item, i) => (
                    <AgreementListItem key={i}>{item}</AgreementListItem>
                  ))}
                </ul>
              )}
            </Box>
          ))}
        </AgreementScrollBox>

        {/* Client Digital Signature Section */}
        <SignatureSection>
          <SignatureSectionTitle>Digital Signature</SignatureSectionTitle>

          <TextField
            label={SIGNATURE_LABEL}
            placeholder={SIGNATURE_PLACEHOLDER}
            value={signedName}
            onChange={handleSignedNameChange}
            fullWidth
            variant="outlined"
            disabled={signing || success}
            inputProps={{ maxLength: SIGNATURE_MAX_LENGTH }}
            error={Boolean(signedNameError)}
            helperText={signedNameError}
            sx={textFieldSx}
          />

          <FormControlLabel
            control={
              <StyledCheckbox
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                disabled={signing || success}
              />
            }
            label={<CheckboxLabel>{CHECKBOX_LABEL}</CheckboxLabel>}
            sx={{ alignItems: "flex-start", mb: 1 }}
          />

          <DateDisplay>Date: {todayFormatted}</DateDisplay>
        </SignatureSection>

        {/* Parent / Legal Guardian Section (only for minors 14-17) */}
        {isMinor && (
          <GuardianSection>
            <GuardianSectionTitle>
              {GUARDIAN_SECTION_TITLE}
            </GuardianSectionTitle>

            <GuardianNotice>
              <GuardianNoticeText>
                {GUARDIAN_SECTION_NOTICE}
              </GuardianNoticeText>
            </GuardianNotice>

            <TextField
              label={GUARDIAN_NAME_LABEL}
              placeholder={GUARDIAN_NAME_PLACEHOLDER}
              value={guardian.name}
              onChange={handleGuardianNameChange}
              fullWidth
              variant="outlined"
              disabled={signing || success}
              inputProps={{ maxLength: SIGNATURE_MAX_LENGTH }}
              error={Boolean(guardianNameError)}
              helperText={guardianNameError}
              sx={textFieldSx}
            />

            <TextField
              label={GUARDIAN_SIGNATURE_LABEL}
              placeholder={GUARDIAN_SIGNATURE_PLACEHOLDER}
              value={guardian.signature}
              onChange={handleGuardianSignatureChange}
              fullWidth
              variant="outlined"
              disabled={signing || success}
              inputProps={{ maxLength: SIGNATURE_MAX_LENGTH }}
              error={Boolean(guardianSignatureError)}
              helperText={guardianSignatureError}
              sx={textFieldSx}
            />

            <TextField
              select
              label={GUARDIAN_RELATIONSHIP_LABEL}
              value={guardian.relationship}
              onChange={(e) => updateGuardian("relationship", e.target.value)}
              fullWidth
              variant="outlined"
              disabled={signing || success}
              sx={textFieldSx}
            >
              {GUARDIAN_RELATIONSHIP_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label={GUARDIAN_EMAIL_LABEL}
              placeholder={GUARDIAN_EMAIL_PLACEHOLDER}
              type="email"
              value={guardian.email}
              onChange={(e) => updateGuardian("email", e.target.value)}
              fullWidth
              variant="outlined"
              disabled={signing || success}
              error={
                guardian.email.trim().length > 0 &&
                !isValidEmail(guardian.email.trim())
              }
              helperText={
                guardian.email.trim().length > 0 &&
                !isValidEmail(guardian.email.trim())
                  ? "Please enter a valid email address"
                  : ""
              }
              sx={textFieldSx}
            />

            <DateDisplay>Date: {todayFormatted}</DateDisplay>
          </GuardianSection>
        )}

        {/* Error display */}
        {error && (
          <AgreementText
            sx={{ color: "#D32F2F", fontWeight: 600, mt: 1.5 }}
          >
            {error}
          </AgreementText>
        )}

        {/* Actions */}
        <ActionBox>
          <CancelButton
            text={CANCEL_BUTTON_TEXT}
            noIcon
            onClick={handleClose}
            disabled={signing}
          />
          <SignButton
            text={signing ? SIGNING_TEXT : SIGN_BUTTON_TEXT}
            noIcon
            onClick={handleSign}
            disabled={!canSign || success}
          />
        </ActionBox>
      </StyledDialogContent>
    </Dialog>
  );
};

export default AgreementSigningModal;
